import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchDrawerReviewHighlights } from "pages/Welcome/fetchDrawerReviewHighlights";

function buildWelcomeDrawerReviewsKey(reviews) {
  if (!Array.isArray(reviews) || !reviews.length) {
    return "";
  }
  return reviews
    .map((review) =>
      String(review?.date_gpid ?? review?.gpid ?? review?.internalId ?? ""),
    )
    .filter(Boolean)
    .join("|");
}

/**
 * Load dashboard highlights (sequences, classifier decisions, topic metadata)
 * for Welcome drawer review cards — same data path as KPI view / console ReviewsList.
 */
export function useWelcomeDrawerReviewHighlights(
  reviews,
  { enabled = true, notifyOnError = false } = {},
) {
  const reviewsKey = useMemo(() => buildWelcomeDrawerReviewsKey(reviews), [reviews]);
  const [highlights, setHighlights] = useState({});
  const [highlightsLoading, setHighlightsLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const getHighlights = useCallback(
    async (reviewsOverride) => {
      const list = reviewsOverride ?? reviews;
      if (!Array.isArray(list) || !list.length) {
        setHighlights({});
        return {};
      }
      setHighlightsLoading(true);
      try {
        const merged = await fetchDrawerReviewHighlights(list, {
          notifyOnError: true,
        });
        setHighlights(merged || {});
        return merged || {};
      } catch {
        setHighlights({});
        return {};
      } finally {
        setHighlightsLoading(false);
      }
    },
    [reviews],
  );

  const refetchHighlights = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !reviewsKey) {
      setHighlights({});
      setHighlightsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setHighlightsLoading(true);

    fetchDrawerReviewHighlights(reviews, { notifyOnError })
      .then((merged) => {
        if (!cancelled) {
          setHighlights(merged || {});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHighlights({});
        }
      })
      .finally(() => {
        if (!cancelled) {
          setHighlightsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, notifyOnError, reviewsKey, reviews, reloadToken]);

  return { highlights, highlightsLoading, refetchHighlights, getHighlights };
}
